import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, User } from "lucide-react";

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedTier = searchParams.get("tier");
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");

  // Handle OAuth callback: if user is already authenticated, redirect
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await navigateAfterAuth(session.user.id, false);
      }
    };

    // Listen for auth state changes (fires when OAuth tokens are processed from URL hash)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        await navigateAfterAuth(session.user.id, false);
      }
    });

    // Also check immediately (in case session already exists)
    checkSession();

    return () => subscription.unsubscribe();
  }, []);

  const navigateAfterAuth = async (userId: string, isNewUser: boolean) => {
    // Check if terms are accepted
    const { data: profile } = await supabase
      .from("profiles")
      .select("subscription_tier_id, terms_accepted_at")
      .eq("user_id", userId)
      .single();

    if (!profile?.terms_accepted_at) {
      navigate("/accept-terms");
      return;
    }

    if (isNewUser || !profile?.subscription_tier_id) {
      navigate(preselectedTier ? `/select-plan?tier=${preselectedTier}` : "/select-plan");
    } else {
      navigate("/dashboard");
    }
  };

  const [showForgot, setShowForgot] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Introdu adresa de email mai întâi");
      return;
    }
    setForgotLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success("Verifică emailul pentru link-ul de resetare!");
      setShowForgot(false);
    } catch (err: any) {
      toast.error(err.message || "A apărut o eroare");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Bun venit înapoi!");
        await navigateAfterAuth(data.user.id, false);
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: fullName } },
        });
        if (error) throw error;
        if (data.session) {
          toast.success("Cont creat cu succes!");
          await navigateAfterAuth(data.user!.id, true);
        } else {
          toast.success("Verifică emailul pentru confirmare!");
        }
      }
    } catch (err: any) {
      toast.error(err.message || "A apărut o eroare");
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthLogin = async (provider: "google" | "apple") => {
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin + "/auth",
      });
      if (result.error) {
        toast.error(`Eroare la autentificarea ${provider}`);
        return;
      }
      if (result.redirected) return;
      // After OAuth callback, check if user needs plan selection
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await navigateAfterAuth(session.user.id, false);
      } else {
        navigate("/dashboard");
      }
    } catch {
      toast.error("A apărut o eroare");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => handleOAuthLogin("google");
  const handleAppleAuth = () => handleOAuthLogin("apple");

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">

        <div className="text-center mb-8">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} className="font-heading text-2xl font-bold text-primary hover:opacity-80 transition-opacity cursor-pointer mb-2 inline-block">EZ Factura</a>
          <p className="text-muted-foreground">
            {isLogin ? "Conectează-te la contul tău" : "Creează un cont nou"}
          </p>
        </div>

        <div className="flex flex-col gap-3 mb-6">
          <Button
            variant="outline"
            className="w-full h-11"
            onClick={handleGoogleAuth}
            disabled={loading}
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continuă cu Google
          </Button>
          <Button
            variant="outline"
            className="w-full h-11"
            onClick={handleAppleAuth}
            disabled={loading}
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Continuă cu Apple
          </Button>
        </div>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">sau cu email</span>
          </div>
        </div>

        <form onSubmit={handleEmailAuth} className="space-y-4">
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="name">Nume complet</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="name"
                  placeholder="Ion Popescu"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="tu@exemplu.ro"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Parolă</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10"
                minLength={6}
                required
              />
            </div>
          </div>
          {isLogin && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={forgotLoading}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                {forgotLoading ? "Se trimite..." : "Ai uitat parola?"}
              </button>
            </div>
          )}
          <Button type="submit" className="w-full h-11 font-semibold" disabled={loading}>
            {loading ? "Se procesează..." : isLogin ? "Conectează-te" : "Creează Cont"}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {isLogin ? "Nu ai cont?" : "Ai deja cont?"}{" "}
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-primary hover:underline font-medium"
          >
            {isLogin ? "Înregistrează-te" : "Conectează-te"}
          </button>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
